import React, { useEffect, useState, useMemo } from 'react';
import { api, fmt } from '../utils/api';
import Topbar from '../components/Topbar';
import { EmptyState, Spinner, useToast, Modal } from '../components/UI';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function parseMonthDate(m) {
  if (!m) return 0;
  const p = String(m).split(' ');
  return parseInt(p[1]||0)*12 + MONTH_NAMES.indexOf(p[0]);
}
function totalExp(m) {
  return (parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0);
}

export default function InvestorPortal({ user, view, setView }) {
  const [franchises, setFranchises] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToast, Toast] = useToast();
  const [withdrawModal, setWithdrawModal] = useState(null);
  const [withdrawForm, setWithdrawForm] = useState({ amount:'', method:'Bank Transfer', date:'', notes:'' });

  useEffect(() => {
    Promise.all([api.getFranchises(), api.getProfit()]).then(([f, p]) => {
      const franchiseMap = {};
      (f||[]).forEach(fr => { franchiseMap[fr.id] = fr; });
      const mergedProfit = (p||[]).map(pp => ({
        ...pp,
        financials: franchiseMap[pp.franchiseId]?.financials || [],
        salaries: franchiseMap[pp.franchiseId]?.salaries || [],
      }));
      setFranchises(f||[]);
      setProfitData(mergedProfit);
      setLoading(false);
    });
  }, []);

  const userEmail = (user?.email||'').toLowerCase();

  // My franchise investments
  const myFranchises = franchises.filter(f =>
    (f.investors||[]).some(i => (i.email||'').toLowerCase() === userEmail)
  );

  const portfolio = myFranchises.map(f => {
    const inv = (f.investors||[]).find(i => (i.email||'').toLowerCase() === userEmail);
    const share = parseFloat(inv?.sharePercent||0) / 100;
    const fin = f.financials||[];
    const latestFin = fin.sort((a,b)=>parseMonthDate(b.month)-parseMonthDate(a.month))[0];
    const latestProfit = latestFin ? (parseFloat(latestFin.income)||0 - totalExp(latestFin)) * share : 0;
    const totalProfit = fin.reduce((s,m) => s + ((parseFloat(m.income)||0 - totalExp(m)) * share), 0);
    return { ...f, share: inv?.sharePercent, latestProfit, totalProfit, inv };
  });

  const totalMonthly = portfolio.reduce((s,p) => s + p.latestProfit, 0);
  const totalAllTime = portfolio.reduce((s,p) => s + p.totalProfit, 0);

  // Chart data - last 12 months across all investments
  const chartData = useMemo(() => {
    const months = {};
    portfolio.forEach(f => {
      (f.financials||[]).forEach(m => {
        if (!months[m.month]) months[m.month] = { month:m.month, profit:0 };
        months[m.month].profit += ((parseFloat(m.income)||0) - totalExp(m)) * parseFloat(f.share||0)/100;
      });
    });
    return Object.values(months).sort((a,b)=>parseMonthDate(a.month)-parseMonthDate(b.month)).slice(-12);
  }, [portfolio]);

  function exportCSV() {
    const rows = [['Franchise','Month','Share%','Profit']];
    portfolio.forEach(f => {
      (f.financials||[]).forEach(m => {
        const profit = ((parseFloat(m.income)||0) - totalExp(m)) * parseFloat(f.share||0)/100;
        rows.push([f.location||f.name, m.month, f.share, profit.toFixed(2)]);
      });
    });
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = (user?.name||'investor').replace(' ','_') + '_portfolio.csv';
    a.click();
  }

  async function saveWithdrawal() {
    if (!withdrawForm.amount || !withdrawForm.date) { showToast('Please enter amount and date'); return; }
    await api.postWithdrawal({
      franchiseId: withdrawModal.franchiseId,
      userId: withdrawModal.userId,
      userEmail: user.email,
      amount: parseFloat(withdrawForm.amount),
      method: withdrawForm.method,
      dateTaken: withdrawForm.date,
      notes: withdrawForm.notes||null,
    });
    await api.logActivity('Investor profit withdrawal', `${user.name} — ${withdrawModal.franchiseName} — £${withdrawForm.amount}`);
    showToast('Withdrawal recorded');
    setWithdrawModal(null);
    setWithdrawForm({ amount:'', method:'Bank Transfer', date:'', notes:'' });
  }

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="My Portfolio" /><Spinner /></div>;

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="My Portfolio" subtitle={`${myFranchises.length} investment${myFranchises.length!==1?'s':''}`} actions={
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={exportCSV}>
            <i className="ti ti-download" style={{ fontSize:13 }} /> CSV
          </button>
        </div>
      } />
      {Toast}

      <div style={{ padding:24 }}>
        {/* Summary KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
          <div className="card" style={{ padding:'14px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>INVESTMENTS</div>
            <div style={{ fontSize:24, fontWeight:700, color:'var(--navy)' }}>{myFranchises.length}</div>
          </div>
          <div className="card" style={{ padding:'14px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>LATEST MONTH PROFIT</div>
            <div style={{ fontSize:24, fontWeight:700, color:totalMonthly>=0?'var(--green)':'var(--red)' }}>{fmt(totalMonthly)}</div>
          </div>
          <div className="card" style={{ padding:'14px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>ALL TIME PROFIT</div>
            <div style={{ fontSize:24, fontWeight:700, color:totalAllTime>=0?'var(--green)':'var(--red)' }}>{fmt(totalAllTime)}</div>
          </div>
        </div>

        {/* Trend chart */}
        {chartData.length > 1 && (
          <div className="card" style={{ padding:20, marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', marginBottom:12, letterSpacing:'0.05em' }}>PORTFOLIO PROFIT TREND</div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,25,96,0.05)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize:10, fill:'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize:10, fill:'var(--text-tertiary)' }} axisLine={false} tickLine={false} tickFormatter={v=>`£${Math.round(v/1000)}k`} />
                <Tooltip formatter={(v)=>[fmt(v),'Profit']} contentStyle={{ fontSize:12, borderRadius:8, border:'1px solid var(--border)' }} />
                <Line type="monotone" dataKey="profit" name="My Profit" stroke="var(--green)" strokeWidth={2.5} dot={{ fill:'var(--green)', r:4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Portfolio cards */}
        {portfolio.length === 0 ? (
          <EmptyState icon="ti-briefcase" message="No investments found" />
        ) : portfolio.map(f => {
          const myProfit = profitData.find(p => p.franchiseId === f.id);
          const myPerson = [...(myProfit?.owners||[]), ...(myProfit?.investors||[])].find(p => (p.email||'').toLowerCase() === userEmail);
          const remaining = myPerson ? (parseFloat(myPerson.totalOwed)||0) - (parseFloat(myPerson.totalTaken)||0) : 0;

          return (
            <div key={f.id} className="card" style={{ marginBottom:14, overflow:'hidden' }}>
              {/* Header */}
              <div style={{ padding:'14px 18px', background:'rgba(28,25,96,0.03)', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:'var(--navy)' }}>{f.location||f.name}</div>
                  {f.location && <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>{f.name}</div>}
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>{f.share}% share</span>
                </div>
              </div>

              {/* Profit summary */}
              {myPerson && (
                <div style={{ padding:'14px 18px', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, borderBottom:'1px solid var(--border)' }}>
                  {myPerson.openingBalance > 0 && (
                    <div>
                      <div style={{ fontSize:10, color:'var(--amber)', letterSpacing:'0.07em', marginBottom:4 }}>OPENING BAL.</div>
                      <div style={{ fontSize:15, fontWeight:700, color:'var(--amber)' }}>{fmt(myPerson.openingBalance)}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize:10, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:4 }}>TOTAL OWED</div>
                    <div style={{ fontSize:15, fontWeight:700, color:'var(--navy)' }}>{fmt(myPerson.totalOwed)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:4 }}>TAKEN</div>
                    <div style={{ fontSize:15, fontWeight:600, color:'var(--text-secondary)' }}>{fmt(myPerson.totalTaken)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:4 }}>REMAINING</div>
                    <div style={{ fontSize:18, fontWeight:800, color:remaining>=0?'var(--green)':'var(--red)' }}>{fmt(remaining)}</div>
                  </div>
                </div>
              )}

              {/* Monthly breakdown */}
              {(f.financials||[]).length > 0 && (
                <div style={{ padding:'10px 18px 14px' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'var(--text-tertiary)', marginBottom:8, letterSpacing:'0.07em' }}>MONTHLY BREAKDOWN</div>
                  <table className="table">
                    <thead><tr><th>MONTH</th><th style={{ textAlign:'right' }}>INCOME</th><th style={{ textAlign:'right' }}>NET PROFIT</th><th style={{ textAlign:'right' }}>MY SHARE ({f.share}%)</th></tr></thead>
                    <tbody>
                      {(f.financials||[]).sort((a,b)=>parseMonthDate(b.month)-parseMonthDate(a.month)).slice(0,6).map(m => {
                        const net = (parseFloat(m.income)||0) - totalExp(m);
                        const myShare = net * parseFloat(f.share||0)/100;
                        return (
                          <tr key={m.month}>
                            <td style={{ fontSize:12 }}>{m.month}</td>
                            <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(m.income)}</td>
                            <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:net>=0?'var(--blue)':'var(--red)' }}>{fmt(net)}</td>
                            <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:700, color:myShare>=0?'var(--green)':'var(--red)' }}>{fmt(myShare)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Withdraw Modal */}
      {withdrawModal && (
        <Modal title="Take Profit" onClose={() => setWithdrawModal(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setWithdrawModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveWithdrawal}>Save</button></>}>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><label>AMOUNT (£) *</label><input type="number" value={withdrawForm.amount} onChange={e=>setWithdrawForm(f=>({...f,amount:e.target.value}))} /></div>
              <div><label>DATE *</label><input type="date" value={withdrawForm.date} onChange={e=>setWithdrawForm(f=>({...f,date:e.target.value}))} /></div>
            </div>
            <div><label>METHOD</label>
              <div style={{ display:'flex', gap:8 }}>
                {['Bank Transfer','Card','Cash','Cheque'].map(m=>(
                  <button key={m} type="button" onClick={()=>setWithdrawForm(f=>({...f,method:m}))} className="btn btn-sm" style={{ background:withdrawForm.method===m?'var(--navy)':'#fff', color:withdrawForm.method===m?'#fff':'var(--text-secondary)', border:'1px solid var(--border)' }}>{m}</button>
                ))}
              </div>
            </div>
            <div><label>NOTES</label><input value={withdrawForm.notes} onChange={e=>setWithdrawForm(f=>({...f,notes:e.target.value}))} /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
